// Auto-extracted by Phase 2 Round 2 (verbatim). See MIGRATION_PLAN §6.
import React from 'react';
import type {CSSProperties} from 'react';
import Button from '../shared/Button.js';
import Segmented, {YesNo} from '../shared/Segmented.js';
import {groupColor, StatusChipRow} from '../shared/GroupPillPicker.js';
import {FilterChips, SearchInput} from '../shared/Toolbar.js';
import {matchesDailySearch, sortDailyRowsByDay} from './ruminantDailyListControls.js';
import {RemoveRowButton} from '../shared/formPatterns.js';
import {CATTLE_HERD_KEYS} from '../lib/cattleHerdFilters.js';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {recordSeqNavOptions, dailySeqItems} from '../lib/recordSequence.js';
import {S} from '../lib/styles.js';
import {softDeleteDailyReport, canDeleteDailyReport, updateDailyReport} from '../lib/dailyReportsApi.js';
import type {DailyReportRecord} from '../lib/dailyReportsApi.js';
import {friendlyDailyDbError} from '../lib/dailyDuplicateCheck.js';
import {dailyReportEditPatch} from '../lib/dailyReportEditPatch.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {buildCattleDailyExportColumns} from '../lib/dailyReportExports.js';
import type {DailyFeedEntry, DailyMineralEntry, DailyReportRow} from '../lib/dailyReportExports.js';
import {printRows} from '../lib/printExport.js';
import {createSavedView, updateSavedView, deleteSavedView} from '../lib/savedViewsApi.js';
// Loaded on the first Add Report click (LazyAdminAddReportModal), not at startup.
import AdminAddReportModal from '../shared/LazyAdminAddReportModal.tsx';
import DailyPhotoThumbnails from '../shared/DailyPhotoThumbnails.jsx';
import OperationalListEmptyState from '../shared/OperationalListEmptyState.jsx';
import InlineNotice from '../shared/InlineNotice.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import {DailyCardList, feedListVal, voltageVal, check, mortText, commentText} from '../shared/DailyRecordCards.jsx';
import {LockedTeamMemberField} from '../shared/recordPageControls.jsx';
import type {DailyPhotoRef} from '../shared/DailyPhotoThumbnails.jsx';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
const CattleDailyPage = React.lazy(() => import('./CattleDailyPage.tsx'));
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import {matchesTeamMemberFilter, teamMemberOptions, teamMemberSelectValue} from '../lib/teamMemberFilter.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import {usePaginatedResource} from '../app/data/usePaginatedResource.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import type {AuthIdentity} from '../contexts/AuthContext.jsx';
import {useDailysRecent} from '../contexts/DailysRecentContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import Header from '../shared/Header.jsx';
import {reconcileCattleDailysRecent, removeCattleDailyFromRecent} from './cattleDailysRecent.js';

const CATTLE_DAILYS_SURFACE_KEY = 'cattle.dailys';
const VALID_CATTLE_DAILY_SOURCE_FILTERS = new Set(['all', 'daily', 'addfeed']);

type ErrorLike = {message?: string};
type PendingDailyEdit = {viewName?: string; id?: string};
type CattleDailyFeed = DailyFeedEntry & {
  feed_input_id?: string | null;
  is_creep?: boolean | null;
  nutrition_snapshot?: Record<string, unknown> | null;
};
type CattleDailyMineral = DailyMineralEntry & {feed_input_id?: string | null};
type CattleDailyRecord = DailyReportRecord &
  Omit<DailyReportRow, 'id' | 'date' | 'herd' | 'team_member' | 'source' | 'feeds' | 'minerals' | 'photos'> & {
    id: string;
    date: string;
    herd: string;
    team_member?: string | null;
    source?: string | null;
    feeds?: CattleDailyFeed[] | null;
    minerals?: CattleDailyMineral[] | null;
    photos?: DailyPhotoRef[] | null;
  };
type CattleFeedInput = {
  id: string;
  name: string;
  category?: string | null;
  status?: string | null;
  herd_scope?: string[] | null;
  unit?: string | null;
  unit_weight_lbs?: number | string | null;
  moisture_pct?: number | string | null;
  nfc_pct?: number | string | null;
  protein_pct?: number | string | null;
};
type DailyFeedDraft = {feedId: string; qty: string; isCreep: boolean};
type DailyMineralDraft = {feedId: string; lbs: string};
type CattleDailyForm = {
  date: string;
  teamMember: string;
  herd: string;
  feeds: DailyFeedDraft[];
  minerals: DailyMineralDraft[];
  fenceVoltage: string;
  waterChecked: boolean;
  mortalityCount: string;
  mortalityReason: string;
  issues: string;
  photos: DailyPhotoRef[];
};
type CattleDailysViewState = {
  fHerd: string;
  fTeam: string;
  fFrom: string;
  fTo: string;
  srcFilter: string;
};
type SavedViewRow = {
  id: string;
  name: string;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: CattleDailysViewState | null;
};

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function CattleDailysRouter() {
  const {authState} = useAuth();
  const location = useLocation();
  const dailyId = location.pathname.startsWith('/cattle/dailys/')
    ? location.pathname.slice('/cattle/dailys/'.length) || null
    : null;
  if (dailyId) {
    return React.createElement(
      React.Suspense,
      {
        fallback: React.createElement(
          'div',
          {role: 'status', 'aria-live': 'polite', style: {padding: '2rem', textAlign: 'center'}},
          'Loading Cattle Daily Record…',
        ),
      },
      React.createElement(CattleDailyPage, {
        sb,
        fmt,
        authState,
      }),
    );
  }
  return React.createElement(CattleDailysHub);
}

const CattleDailysHub = () => {
  const {authState} = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState'> & {
    // The guarded expression below must remain byte-stable; optional chaining
    // preserves the context's null/false runtime states while this assertion
    // supplies the resolved identity shape to TypeScript.
    authState: AuthIdentity;
  };
  const {pendingEdit: pendingEditValue, setPendingEdit} = useUI();
  const pendingEdit = pendingEditValue as PendingDailyEdit | null;
  const {setCattleDailysRecent} = useDailysRecent();
  const {useState, useEffect, useMemo} = React;
  const [showForm, setShowForm] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editSource, setEditSource] = useState<string | null>(null);
  const [form, setForm] = useState<CattleDailyForm | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [reloadKey, setReloadKey] = useState<number>(0);
  // Query owns the immutable page sequence. Successful writes project small
  // local overlays over it so the list reconciles immediately without an App
  // setter or a global refresh closure.
  const [recordOverrides, setRecordOverrides] = useState<Record<string, CattleDailyRecord>>({});
  const [hiddenRecordIds, setHiddenRecordIds] = useState<Record<string, true>>({});
  const [createdRecords, setCreatedRecords] = useState<CattleDailyRecord[]>([]);
  const [exportNotice, setExportNotice] = useState<string>('');
  const [savedViewsRefreshError, setSavedViewsRefreshError] = useState<string | null>(null);
  const [savedViewsRefreshing, setSavedViewsRefreshing] = useState<boolean>(false);
  const [selectedViewId, setSelectedViewId] = useState<string>('');
  const [showSaveViewForm, setShowSaveViewForm] = useState<boolean>(false);
  // R17: Saved Views and Filters are pop-outs off the one toolbar row. Which
  // one is open is transient, like Phase 2's broiler batches toolbar.
  const [openToolPanel, setOpenToolPanel] = useState<'search' | 'savedViews' | 'filters' | 'sort' | 'columns' | null>(
    null,
  );
  const [saveViewName, setSaveViewName] = useState<string>('');
  const [saveViewVisibility, setSaveViewVisibility] = useState<'private' | 'public'>('private');
  const [savedViewBusy, setSavedViewBusy] = useState<boolean>(false);
  const [savedViewNotice, setSavedViewNotice] = useState<Notice | null>(null);
  const myProfileId = authState?.user?.id || null;
  // Tier-2 write boundary (migration 192): AdminAddReportModal stamps
  // source='admin_add_report', and submit_cattle_daily_report scopes that
  // provenance to management + admin. Light and farm_team keep submitting
  // cattle dailies through the /dailys hub (source='daily_webform'); showing
  // them an Add Report button the server refuses would be a dead end.
  // The server RPC is the enforcement — this only hides an affordance.
  const canAddAdminReport = ['management', 'admin'].includes(authState?.role);
  const [fHerd, setFHerd] = usePersistentViewState('cattle.dailys.herdFilter', '');
  const [fTeam, setFTeam] = usePersistentViewState('cattle.dailys.teamFilter', '');
  const [fFrom, setFFrom] = usePersistentViewState('cattle.dailys.fromFilter', '');
  const [fTo, setFTo] = usePersistentViewState('cattle.dailys.toFilter', '');
  const [srcFilter, setSrcFilter] = usePersistentViewState('cattle.dailys.sourceFilter', 'all');
  // R17 Search and Sort, kept for the session (L02). Their defaults - no
  // search, newest day first, rows within a day as loaded - are exactly the
  // list as it was. They are not part of the saved-view state.
  const [search, setSearch] = usePersistentViewState('cattle.dailys.search', '');
  const [sortDir, setSortDir] = usePersistentViewState('cattle.dailys.sortDir', 'desc');
  const [sortWithin, setSortWithin] = usePersistentViewState('cattle.dailys.sortWithin', 'loaded');
  // R17 Columns: the card parts this list hides (shared DailyRecordCards
  // hiddenColumns). Empty - every part shown - is the default.
  const [hiddenColumns, setHiddenColumns] = usePersistentViewState('cattle.dailys.hiddenColumns', [] as string[]);

  const HERDS = ['mommas', 'backgrounders', 'finishers', 'bulls', 'processed', 'deceased', 'sold'];
  const HERD_LABELS: Record<string, string> = {
    mommas: 'Mommas',
    backgrounders: 'Backgrounders',
    finishers: 'Finishers',
    bulls: 'Bulls',
    processed: 'Processed',
    deceased: 'Deceased',
    sold: 'Sold',
  };

  const cattleDailysParams = useMemo(() => ({reload: reloadKey}), [reloadKey]);
  const cattleDailys = usePaginatedResource<CattleDailyRecord, number>(RESOURCE.CATTLE_DAILYS_LIST, cattleDailysParams);
  const {
    error: listError,
    hasMore: listHasMore,
    loadMore,
    loadMoreError: listLoadMoreError,
    loadingMore: listLoadingMore,
    retryLoadMore,
  } = cattleDailys;
  const formInputsResource = useRouteResource(RESOURCE.CATTLE_DAILYS_FORM_INPUTS, cattleDailysParams);
  const {
    data: savedViewsDataRaw,
    loading: savedViewsQueryLoading,
    error: savedViewsQueryError,
    refetch: refetchSavedViews,
  } = useRouteResource(RESOURCE.CATTLE_DAILYS_SAVED_VIEWS);
  const loading = cattleDailys.loading;
  const loadError = useMemo<Notice | null>(
    () =>
      listError
        ? {
            kind: 'error',
            message:
              'Could not load daily reports. Please refresh the page. (' + (listError.message || listError) + ')',
          }
        : null,
    [listError],
  );
  const appendError = useMemo<Notice | null>(
    () =>
      listLoadMoreError
        ? {
            kind: 'error',
            message: 'Could not load more daily reports. (' + (listLoadMoreError.message || listLoadMoreError) + ')',
          }
        : null,
    [listLoadMoreError],
  );
  // Preserve the old fail-soft input behavior: an input-list failure leaves
  // history readable and simply gives the edit controls no fresh options.
  const feedInputs = (formInputsResource.data || []) as CattleFeedInput[];
  const savedViewsData = useMemo(() => (savedViewsDataRaw || []) as SavedViewRow[], [savedViewsDataRaw]);
  // Query correctly retains last-good data after a same-key refetch failure.
  // This picker historically cleared its list instead, so retain that local UI
  // contract without reviving a second saved-view read or any App closure.
  const savedViewsError = useMemo<string | null>(() => {
    if (savedViewsRefreshError) return savedViewsRefreshError;
    if (!savedViewsQueryError) return null;
    const error = savedViewsQueryError as ErrorLike;
    return error.message || String(savedViewsQueryError);
  }, [savedViewsQueryError, savedViewsRefreshError]);
  const savedViews = savedViewsError ? [] : savedViewsData;
  // Same-key Query refetches do not become `loading`; keep the prior picker
  // timing by disabling it while an action-time saved-view refresh is pending.
  const savedViewsLoading = savedViewsQueryLoading || savedViewsRefreshing;
  const records = useMemo(() => {
    const seen = new Set<string>();
    const rows: CattleDailyRecord[] = [];
    for (const row of [...createdRecords, ...cattleDailys.rows]) {
      if (hiddenRecordIds[row.id] || seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(recordOverrides[row.id] || row);
    }
    return rows;
  }, [cattleDailys.rows, createdRecords, hiddenRecordIds, recordOverrides]);

  // A Retry is a new server sequence. Its rows are authoritative again, so no
  // transient local mutation projection may leak into the new key.
  useEffect(() => {
    setRecordOverrides({});
    setHiddenRecordIds({});
    setCreatedRecords([]);
  }, [reloadKey]);

  // Keep the pre-existing eager "load every page" behavior while delegating
  // cursors, cancellation, and stale-key protection to the paginated runtime.
  useEffect(() => {
    if (listHasMore && !listLoadingMore && !listLoadMoreError) {
      void loadMore();
    }
  }, [listHasMore, listLoadingMore, listLoadMoreError, loadMore]);

  useEffect(() => {
    if (savedViewsQueryLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViewsData.some((row) => row.id === cur) ? cur : ''));
  }, [savedViewsData, savedViewsError, savedViewsQueryLoading]);

  const reloadSavedViews = React.useCallback(async () => {
    setSavedViewsRefreshing(true);
    try {
      const result = await refetchSavedViews();
      // Before identity settles, useRouteResource deliberately resolves a
      // refetch without issuing a request. Preserve the current picker rather
      // than turning that no-op into a fabricated empty success.
      if (!result || typeof result !== 'object') return;
      const {data, error} = result as {data?: unknown; error?: unknown};
      if (error) {
        const caught = error as ErrorLike;
        setSavedViewsRefreshError(caught.message || String(error));
        return;
      }
      const rows = (data || []) as SavedViewRow[];
      setSavedViewsRefreshError(null);
      setSelectedViewId((cur) => (cur && rows.some((row) => row.id === cur) ? cur : ''));
    } catch (caught) {
      const error = caught as ErrorLike;
      setSavedViewsRefreshError(error.message || String(caught));
    } finally {
      setSavedViewsRefreshing(false);
    }
  }, [refetchSavedViews]);

  const navigate = useNavigate();

  const openEdit = React.useCallback((d: CattleDailyRecord) => {
    setNotice(null);
    setForm({
      date: d.date || todayStr(),
      teamMember: d.team_member || '',
      herd: d.herd || '',
      feeds:
        Array.isArray(d.feeds) && d.feeds.length > 0
          ? d.feeds.map((f) => ({
              feedId: f.feed_input_id || '',
              qty: f.qty != null ? String(f.qty) : '',
              isCreep: !!f.is_creep,
            }))
          : [{feedId: '', qty: '', isCreep: false}],
      minerals:
        Array.isArray(d.minerals) && d.minerals.length > 0
          ? d.minerals.map((m) => ({feedId: m.feed_input_id || '', lbs: m.lbs != null ? String(m.lbs) : ''}))
          : [{feedId: '', lbs: ''}],
      fenceVoltage: d.fence_voltage != null ? String(d.fence_voltage) : '',
      waterChecked: d.water_checked !== false,
      mortalityCount: d.mortality_count != null ? String(d.mortality_count) : '',
      mortalityReason: d.mortality_reason || '',
      issues: d.issues || '',
      // Read-only in v1 — saveEdit does not write photos back.
      photos: Array.isArray(d.photos) ? d.photos : [],
    });
    setEditId(d.id);
    setEditSource(d.source || null);
    setShowForm(true);
  }, []);

  useEffect(() => {
    if (loading || loadError || !pendingEdit || pendingEdit.viewName !== 'cattledailys' || !pendingEdit.id) return;
    const record = records.find((row) => row.id === pendingEdit.id);
    if (!record) return;
    openEdit(record);
    if (setPendingEdit) setPendingEdit(null);
  }, [loadError, loading, openEdit, pendingEdit, records, setPendingEdit]);

  async function saveEdit() {
    setNotice(null);
    const currentForm = form!;
    const currentEditId = editId!;
    if (!currentForm.date || !currentForm.teamMember || !currentForm.herd) {
      setNotice({kind: 'error', message: 'Date, team member, and herd are required.'});
      return;
    }
    if (parseInt(currentForm.mortalityCount) > 0 && !(currentForm.mortalityReason || '').trim()) {
      setNotice({kind: 'error', message: 'Mortality reason is required when mortalities are reported.'});
      return;
    }
    // Build feeds/minerals jsonb with updated nutrition snapshots
    const feedsJ = (currentForm.feeds || [])
      .filter((r) => r.feedId && r.qty !== '' && r.qty != null)
      .map((r) => {
        const fi = feedInputs.find((x) => x.id === r.feedId);
        if (!fi) return null;
        const qty = parseFloat(r.qty) || 0;
        const unitWt = parseFloat(fi.unit_weight_lbs as string) || 1;
        return {
          feed_input_id: fi.id,
          feed_name: fi.name,
          category: fi.category,
          qty: qty,
          unit: fi.unit,
          lbs_as_fed: Math.round(qty * unitWt * 100) / 100,
          is_creep: !!r.isCreep,
          nutrition_snapshot: {moisture_pct: fi.moisture_pct, nfc_pct: fi.nfc_pct, protein_pct: fi.protein_pct},
        };
      })
      .filter(Boolean) as CattleDailyFeed[];
    const mineralsJ = (currentForm.minerals || [])
      .filter((m) => m.feedId && m.lbs !== '' && m.lbs != null)
      .map((m) => {
        const fi = feedInputs.find((x) => x.id === m.feedId);
        if (!fi) return null;
        return {feed_input_id: fi.id, name: fi.name, lbs: parseFloat(m.lbs) || 0};
      })
      .filter(Boolean) as CattleDailyMineral[];
    const rec = dailyReportEditPatch('cattle', editSource, {
      date: currentForm.date,
      team_member: currentForm.teamMember,
      herd: currentForm.herd,
      feeds: feedsJ,
      minerals: mineralsJ,
      fence_voltage: currentForm.fenceVoltage !== '' ? parseFloat(currentForm.fenceVoltage) : null,
      water_checked: currentForm.waterChecked,
      mortality_count: currentForm.mortalityCount !== '' ? parseInt(currentForm.mortalityCount) : 0,
      mortality_reason: currentForm.mortalityReason || null,
      issues: currentForm.issues || null,
    });
    try {
      await updateDailyReport(sb, 'cattle.daily', currentEditId, rec, {entityLabel: rec.date});
    } catch (e) {
      const error = e as ErrorLike;
      setNotice({
        kind: 'error',
        message: 'Save failed: ' + friendlyDailyDbError(error.message || String(e), 'cattle_dailys', rec),
      });
      return;
    }
    const prior = records.find((row) => row.id === currentEditId);
    const updated = {...prior, ...rec, id: currentEditId} as CattleDailyRecord;
    setRecordOverrides((current) => ({...current, [currentEditId]: updated}));
    setCattleDailysRecent((current) => reconcileCattleDailysRecent(current as CattleDailyRecord[], updated));
    setShowForm(false);
    setEditId(null);
    setForm(null);
  }

  function del(id: string) {
    if (!window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete this daily report?', async () => {
      const rec = records.find((r) => r.id === id);
      const label = rec ? rec.date + (rec.herd ? ' · ' + rec.herd : '') : id;
      await softDeleteDailyReport(sb, 'cattle_dailys', id, label);
      setHiddenRecordIds((current) => ({...current, [id]: true}));
      setRecordOverrides((current) => {
        const next = {...current};
        delete next[id];
        return next;
      });
      setCreatedRecords((current) => current.filter((row) => row.id !== id));
      setCattleDailysRecent((current) => removeCattleDailyFromRecent(current as CattleDailyRecord[], id));
      setShowForm(false);
      setEditId(null);
      setForm(null);
    });
  }

  // Split in two so the source chips can count each source under the same
  // date/herd/team filters (R18); the rows shown are exactly what the one
  // combined predicate used to return.
  // Search (L02) narrows like a filter, over fields already loaded: the herd,
  // the team member, the feed and mineral names, and the issues text.
  const matchesSearch = (r: CattleDailyRecord) =>
    matchesDailySearch(
      [
        HERD_LABELS[r.herd] || r.herd,
        r.team_member,
        ...(Array.isArray(r.feeds) ? r.feeds.map((f) => f.feed_name) : []),
        ...(Array.isArray(r.minerals) ? r.minerals.map((m) => m.name) : []),
        r.issues,
      ],
      search,
    );
  const baseFiltered = records.filter(
    (r) =>
      (!fHerd || r.herd === fHerd) &&
      matchesTeamMemberFilter(r.team_member, fTeam) &&
      (!fFrom || r.date >= fFrom) &&
      (!fTo || r.date <= fTo) &&
      matchesSearch(r),
  );
  const sourceRows = baseFiltered.filter(
    (r) =>
      srcFilter === 'all' ||
      (srcFilter === 'daily' && r.source !== 'add_feed_webform') ||
      (srcFilter === 'addfeed' && r.source === 'add_feed_webform'),
  );
  // Sort keeps the R21 day grouping: DailyCardList groups consecutive same-day
  // rows, so the order is always by day (newest or oldest first) and only the
  // order WITHIN a day is chosen. The default is the loaded order untouched.
  // `filtered` is the sorted set, so the rows, the record sequence and CSV/Print
  // all follow it.
  const filtered = sortDailyRowsByDay(
    sourceRows,
    sortDir,
    sortWithin,
    (r) => String(HERD_LABELS[r.herd] || r.herd || ''),
    (r) => String(r.team_member || ''),
  );
  const addFeedCount = baseFiltered.filter((r) => r.source === 'add_feed_webform').length;
  // Counts come from the rows already loaded. The list eagerly loads every
  // page, so they are final once listHasMore clears; until then the summary
  // says so rather than presenting a partial count as the whole.
  const sourceChips = [
    {key: 'all', label: 'All', count: baseFiltered.length},
    {key: 'daily', label: 'Daily Reports', count: baseFiltered.length - addFeedCount},
    {key: 'addfeed', label: 'Add Feed', count: addFeedCount},
  ];
  const teamOpts = teamMemberOptions(records.map((r) => r.team_member));
  // L03: the filters now live in a pop-out, so the active ones show as
  // removable chips under the toolbar.
  const activeFilters = [
    fFrom ? {key: 'from', label: 'From ' + fmt(fFrom), onRemove: () => setFFrom('')} : null,
    fTo ? {key: 'to', label: 'To ' + fmt(fTo), onRemove: () => setFTo('')} : null,
    fHerd ? {key: 'herd', label: HERD_LABELS[fHerd] || fHerd, onRemove: () => setFHerd('')} : null,
    fTeam ? {key: 'team', label: fTeam, onRemove: () => setFTeam('')} : null,
  ].filter((f): f is {key: string; label: string; onRemove: () => void} => f !== null);
  // The filter bar's old Clear, unchanged, now behind the chips' Clear All
  // (L03): it resets the source as well. A source-only reset is the All chip.
  function clearAllFilters() {
    setFHerd('');
    setFTeam('');
    setFFrom('');
    setFTo('');
    setSrcFilter('all');
  }
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);

  function cattleDailysViewState(): CattleDailysViewState {
    return {
      fHerd: fHerd || '',
      fTeam: fTeam || '',
      fFrom: fFrom || '',
      fTo: fTo || '',
      srcFilter: VALID_CATTLE_DAILY_SOURCE_FILTERS.has(srcFilter) ? srcFilter : 'all',
    };
  }

  function applyCattleDailysSavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    const st = (view.view_state || {}) as CattleDailysViewState;
    setFHerd(typeof st.fHerd === 'string' ? st.fHerd : '');
    setFTeam(typeof st.fTeam === 'string' ? st.fTeam : '');
    setFFrom(typeof st.fFrom === 'string' ? st.fFrom : '');
    setFTo(typeof st.fTo === 'string' ? st.fTo : '');
    setSrcFilter(VALID_CATTLE_DAILY_SOURCE_FILTERS.has(st.srcFilter) ? st.srcFilter : 'all');
    setSavedViewNotice(null);
  }

  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    setSavedViewNotice(null);
    if (!id) return;
    applyCattleDailysSavedView(savedViews.find((v) => v.id === id));
  }

  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setSavedViewNotice(null);
    setShowSaveViewForm(true);
  }

  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setSavedViewNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: CATTLE_DAILYS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: cattleDailysViewState(),
      })) as SavedViewRow | null;
      setShowSaveViewForm(false);
      setSavedViewNotice({kind: 'success', message: 'Saved view "' + name + '".'});
      await reloadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (e) {
      const error = e as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Save view failed: ' + (error.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: cattleDailysViewState()});
      await reloadSavedViews();
      setSavedViewNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current filters.',
      });
    } catch (e) {
      const error = e as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Update view failed: ' + (error.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await reloadSavedViews();
      setSavedViewNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (e) {
      const error = e as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Delete view failed: ' + (error.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete saved view "' + selectedView.name + '"?', () => {
      void proceedDeleteSelectedView(selectedView);
    });
  }

  function handleExportCsv() {
    const columns = buildCattleDailyExportColumns({herdLabels: HERD_LABELS});
    const ok = downloadCsv(csvFilename('cattle-dailys'), rowsToCsv(columns, filtered));
    setExportNotice(ok ? '' : 'CSV export is only available in the browser.');
  }

  function handlePrintRows() {
    const columns = buildCattleDailyExportColumns({herdLabels: HERD_LABELS});
    const ok = printRows({
      title: 'Cattle Dailys',
      subtitle: filtered.length + ' filtered daily reports',
      columns,
      rows: filtered,
    });
    setExportNotice(ok ? '' : 'Print is only available in the browser.');
  }

  const fi: CSSProperties = {
    padding: '6px 10px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    fontSize: 12,
    fontFamily: 'inherit',
    background: 'var(--surface)',
    width: 'auto',
  };
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  // The edit modal's "+ Add" beside Feeds / Minerals: brand ink on a neutral
  // pill, the same for both lists. They were a cattle red and a purple, and
  // neither colour meant anything (§1.4: colour means status).
  const modalAddRowS: CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--brand)',
    background: 'none',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--r-pill)',
    padding: '3px 10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const savedViewRadioLabelS: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: 'var(--ink)',
    cursor: 'pointer',
  };
  // The open pop-out panel, and its toggle's open state (Q01: green outline),
  // as Phase 2's broiler batches toolbar draws them.
  const toolPanelS: CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '12px 16px',
    marginBottom: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };
  const toolOpenS: CSSProperties = {borderColor: 'var(--brand)', color: 'var(--brand)'};
  const filterLabelS: CSSProperties = {
    fontSize: 11,
    color: 'var(--ink-muted)',
    display: 'inline-flex',
    flexDirection: 'column',
    gap: 3,
    fontWeight: 600,
  };
  const toggleToolPanel = (panel: 'search' | 'savedViews' | 'filters' | 'sort' | 'columns') =>
    setOpenToolPanel((cur) => (cur === panel ? null : panel));

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-cattle-dailys-loaded={loading || loadError ? 'false' : 'true'}
    >
      <Header />
      {/* An H1, the page's one green on its add action (C04, Q06-B) rather
          than a program-colour fill (program colour is never a button, R05),
          and the shared controls. 1180px, as R19 asks. */}
      <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <h1 className="wcf-page-title">Daily Reports</h1>
            <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>
              {records.length.toLocaleString()} total
            </div>
          </div>
          <div style={{display: 'flex', gap: 8}}>
            {canAddAdminReport && (
              <Button
                variant="primary"
                onClick={() => {
                  setNotice(null);
                  setShowAddModal(true);
                }}
              >
                + Add Report
              </Button>
            )}
          </div>
        </div>
        {showAddModal && (
          <AdminAddReportModal
            sb={sb}
            formType="cattle"
            onClose={() => setShowAddModal(false)}
            onSaved={(recs: CattleDailyRecord | CattleDailyRecord[]) => {
              const created = Array.isArray(recs) ? recs : [recs];
              setCreatedRecords((current) => [
                ...created,
                ...current.filter((row) => !created.some((record) => record.id === row.id)),
              ]);
              setCattleDailysRecent((current) =>
                created.reduce(
                  (recent, record) => reconcileCattleDailysRecent(recent, record),
                  current as CattleDailyRecord[],
                ),
              );
            }}
          />
        )}

        {/* R17: one toolbar row - Search, then Saved Views, Filters and Sort
            as pop-outs, then CSV and Print - drawn as Phase 2's broiler batches
            toolbar. Every control works over the rows already loaded. The
            Saved Views toggle keeps the old row's gating: it is not offered
            while the list itself failed to load. */}
        <div
          data-cattle-dailys-toolbar="1"
          style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8}}
        >
          {/* Search is a pop-out like the rest (R17). An active search names
              itself on the button, so it shows while the panel is closed. */}
          <Button
            type="button"
            data-cattle-dailys-search-toggle="1"
            data-cattle-dailys-search-active={search.trim() ? '1' : '0'}
            aria-expanded={openToolPanel === 'search'}
            onClick={() => toggleToolPanel('search')}
            style={openToolPanel === 'search' || search.trim() ? toolOpenS : undefined}
          >
            {search.trim()
              ? 'Search: ' + (search.trim().length > 18 ? search.trim().slice(0, 18) + '…' : search.trim())
              : 'Search'}
          </Button>
          {!loadError && (
            <Button
              type="button"
              data-cattle-dailys-saved-views-toggle="1"
              aria-expanded={openToolPanel === 'savedViews'}
              onClick={() => toggleToolPanel('savedViews')}
              style={openToolPanel === 'savedViews' ? toolOpenS : undefined}
            >
              Saved Views
            </Button>
          )}
          <Button
            type="button"
            data-cattle-dailys-filters-toggle="1"
            aria-expanded={openToolPanel === 'filters'}
            onClick={() => toggleToolPanel('filters')}
            style={openToolPanel === 'filters' ? toolOpenS : undefined}
          >
            {activeFilters.length > 0 ? 'Filters (' + activeFilters.length + ')' : 'Filters'}
          </Button>
          <Button
            type="button"
            data-cattle-dailys-sort-toggle="1"
            aria-expanded={openToolPanel === 'sort'}
            onClick={() => toggleToolPanel('sort')}
            style={openToolPanel === 'sort' ? toolOpenS : undefined}
          >
            Sort
          </Button>
          <Button
            type="button"
            data-cattle-dailys-columns-toggle="1"
            aria-expanded={openToolPanel === 'columns'}
            onClick={() => toggleToolPanel('columns')}
            style={openToolPanel === 'columns' ? toolOpenS : undefined}
          >
            {hiddenColumns.length > 0 ? 'Columns (' + hiddenColumns.length + ' hidden)' : 'Columns'}
          </Button>
          <Button
            type="button"
            data-cattle-dailys-export-csv="1"
            onClick={handleExportCsv}
            disabled={loading || !!loadError}
          >
            CSV
          </Button>
          <Button
            type="button"
            data-cattle-dailys-print="1"
            onClick={handlePrintRows}
            disabled={loading || !!loadError}
          >
            Print
          </Button>
        </div>

        {!loadError && openToolPanel === 'savedViews' && (
          <div data-cattle-dailys-saved-views-row style={toolPanelS}>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-cattle-dailys-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-cattle-dailys-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...fi, minWidth: 200}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        type="button"
                        data-cattle-dailys-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-cattle-dailys-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  {savedViewNotice && (
                    <span
                      style={{
                        fontSize: 12,
                        color: savedViewNotice.kind === 'success' ? 'var(--ok-ink)' : 'var(--danger-ink)',
                      }}
                    >
                      {savedViewNotice.message}
                    </span>
                  )}
                  <Button
                    type="button"
                    data-cattle-dailys-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy || savedViewsLoading}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>
            {showSaveViewForm && (
              <div
                data-cattle-dailys-saved-view-form
                style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}
              >
                <input
                  data-cattle-dailys-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...fi, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveCattleDailysViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-cattle-dailys-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveCattleDailysViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-cattle-dailys-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button
                  type="button"
                  data-cattle-dailys-saved-view-save
                  onClick={submitSaveView}
                  disabled={savedViewBusy}
                >
                  Save
                </Button>
                <Button type="button" onClick={() => setShowSaveViewForm(false)} disabled={savedViewBusy}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        {openToolPanel === 'filters' && (
          <div data-cattle-dailys-filters="1" style={toolPanelS}>
            <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end'}}>
              <label style={filterLabelS}>
                From
                <input
                  type="date"
                  value={fFrom}
                  onChange={(e) => setFFrom(e.target.value)}
                  style={{...fi, width: 140}}
                />
              </label>
              <label style={filterLabelS}>
                To
                <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={{...fi, width: 140}} />
              </label>
              <label style={filterLabelS}>
                Herd
                <select value={fHerd} onChange={(e) => setFHerd(e.target.value)} style={fi}>
                  <option value="">All herds</option>
                  {HERDS.map((h) => (
                    <option key={h} value={h}>
                      {HERD_LABELS[h]}
                    </option>
                  ))}
                </select>
              </label>
              <label style={filterLabelS}>
                Team member
                <select
                  value={fTeam}
                  onChange={(e) => setFTeam(e.target.value)}
                  style={fi}
                  data-cattle-dailys-team-filter="1"
                >
                  <option value="">All team</option>
                  {teamOpts.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}

        {openToolPanel === 'search' && (
          <div data-cattle-dailys-search="1" style={toolPanelS}>
            <SearchInput
              value={search}
              onChange={setSearch}
              label="Search daily reports"
              placeholder="Search herd, team, feed, issues"
            />
          </div>
        )}

        {openToolPanel === 'columns' && (
          <div data-cattle-dailys-columns="1" style={toolPanelS}>
            {/* Only parts the card actually draws: its DAILY_COLS keys (feed,
                volt) and its two fixed parts (team, checks). Mortality and the
                comment are row annotations (R21) and always show. CSV/Print
                keep their own columns. */}
            <div style={{display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center'}}>
              {(
                [
                  ['team', 'Team member'],
                  ['feed', 'Feed'],
                  ['volt', 'Fence'],
                  ['checks', 'Water check'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} style={savedViewRadioLabelS}>
                  <input
                    type="checkbox"
                    data-cattle-dailys-column-toggle={key}
                    checked={!hiddenColumns.includes(key)}
                    onChange={(e) =>
                      setHiddenColumns((cur) =>
                        e.target.checked ? cur.filter((k) => k !== key) : [...cur.filter((k) => k !== key), key],
                      )
                    }
                  />
                  {label}
                </label>
              ))}
              <Button
                type="button"
                data-cattle-dailys-columns-reset="1"
                onClick={() => setHiddenColumns([])}
                disabled={hiddenColumns.length === 0}
              >
                Show all
              </Button>
            </div>
          </div>
        )}

        {openToolPanel === 'sort' && (
          <div data-cattle-dailys-sort="1" style={toolPanelS}>
            <div style={{display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end'}}>
              {/* Days stay grouped (R21): the date direction is a two-option
                  choice (R29), and only the order within a day is open. */}
              <div style={filterLabelS}>
                Days
                <Segmented
                  aria-label="Day order"
                  value={sortDir}
                  onChange={setSortDir}
                  options={[
                    {value: 'desc', label: 'Newest first'},
                    {value: 'asc', label: 'Oldest first'},
                  ]}
                />
              </div>
              <label style={filterLabelS}>
                Within a day
                <select
                  value={sortWithin}
                  onChange={(e) => setSortWithin(e.target.value)}
                  style={fi}
                  data-cattle-dailys-sort-within="1"
                >
                  <option value="loaded">As entered</option>
                  <option value="group">Herd</option>
                  <option value="team">Team member</option>
                </select>
              </label>
            </div>
          </div>
        )}

        <FilterChips filters={activeFilters} onClearAll={clearAllFilters} />

        {/* R18: the counted source chips replace the Source segmented control;
            selected is a brand outline. Same srcFilter state, default and
            saved-view round trip as before. */}
        <div style={{marginBottom: 12}}>
          <StatusChipRow
            chips={sourceChips}
            selected={srcFilter}
            onSelect={setSrcFilter}
            rootAttrs={{'data-cattle-dailys-source-chips': '1'}}
            summary={
              <span data-cattle-dailys-count="1">
                {filtered.length.toLocaleString() + (filtered.length === 1 ? ' report' : ' reports')}
                {listHasMore ? ' loaded so far' : ''}
              </span>
            }
          />
        </div>
        {exportNotice && <div style={{marginBottom: 14, color: 'var(--danger-ink)', fontSize: 12}}>{exportNotice}</div>}

        <InlineNotice notice={loadError} />
        {loadError && (
          <Button
            type="button"
            data-daily-list-retry="1"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{marginBottom: 12}}
          >
            Retry
          </Button>
        )}
        <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
        <InlineNotice notice={appendError} />
        {appendError && (
          <Button
            type="button"
            data-daily-list-load-more-retry="1"
            onClick={() => void retryLoadMore()}
            style={{marginBottom: 12}}
          >
            Retry loading more
          </Button>
        )}
        {loading && (
          <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading{'\u2026'}</div>
        )}
        <OperationalListEmptyState
          loading={loading}
          loadError={loadError}
          totalCount={records.length}
          filteredCount={filtered.length}
          emptyLabel="No cattle daily reports yet"
        />
        {!loading && !loadError && filtered.length > 0 && (
          <DailyCardList
            program="cattle"
            rows={filtered}
            hiddenColumns={hiddenColumns}
            fmt={fmt}
            maxInitialRows={100}
            onOpen={(d) => navigate('/cattle/dailys/' + d.id, recordSeqNavOptions(dailySeqItems(filtered, 'herd')))}
            rowAttrs={(d) => ({'data-daily-row': d.id})}
            mapRow={(d) => {
              const row = d as CattleDailyRecord;
              // R06: the herd dot is the fixed group palette, index = the
              // herd's position in CATTLE_HERD_KEYS. An outcome herd
              // (processed/deceased/sold) is not one of the six groups, so it
              // takes the faint neutral - the same rule RuminantGroupLabel
              // applies for an archived group on the herds list.
              const herdIndex = CATTLE_HERD_KEYS.indexOf(row.herd);
              return {
                name: HERD_LABELS[row.herd] || row.herd,
                dot: herdIndex >= 0 ? groupColor(herdIndex) : 'var(--ink-faint)',
                team: row.team_member || '—',
                source: row.source,
                photos: row.photos,
                vals: {
                  feed: feedListVal(row.feeds, row.minerals),
                  volt: voltageVal(row.fence_voltage),
                },
                checks: [check('Water', row.water_checked !== false)],
                mort: mortText(row.mortality_count, row.mortality_reason),
                comment: commentText(row.issues),
              };
            }}
          />
        )}
        {listHasMore && !appendError && (
          <div style={{textAlign: 'center', padding: '0.5rem', fontSize: 11, color: 'var(--ink-faint)'}}>
            Loading more records{'\u2026'}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {showForm && form && (
        <div
          onClick={() => {
            setNotice(null);
            setShowForm(false);
            setEditId(null);
            setForm(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--scrim)',
            zIndex: 500,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '1rem',
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: 'var(--r-modal)',
              width: '100%',
              maxWidth: 520,
              boxShadow: 'var(--shadow-modal)',
              marginTop: 40,
            }}
          >
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{fontSize: 15, fontWeight: 700, color: 'var(--ink)'}}>
                {editSource === 'add_feed_webform' ? 'Edit Cattle Add Feed Report' : 'Edit Cattle Daily Report'}
              </div>
              <button
                onClick={() => {
                  setNotice(null);
                  setShowForm(false);
                  setEditId(null);
                  setForm(null);
                }}
                style={{background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--ink-faint)'}}
              >
                {'\u00d7'}
              </button>
            </div>
            <div
              data-mobile-1col="1"
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
                maxHeight: '70vh',
                overflowY: 'auto',
              }}
            >
              <div style={{gridColumn: '1/-1'}}>
                <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <label style={S.label}>Date *</label>
                <input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} />
              </div>
              <div style={{gridColumn: '1/-1'}}>
                {React.createElement(LockedTeamMemberField, {
                  value: form.teamMember,
                  label: 'Team Member *',
                  labelStyle: S.label,
                  style: {maxWidth: '100%'},
                })}
              </div>
              <div style={{gridColumn: '1/-1'}}>
                <label style={S.label}>Herd *</label>
                <select value={form.herd} onChange={(e) => setForm({...form, herd: e.target.value})}>
                  <option value="">Select herd...</option>
                  {HERDS.map((h) => (
                    <option key={h} value={h}>
                      {HERD_LABELS[h]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Feeds */}
              <div style={{gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 10}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
                  <span style={{fontSize: 11, fontWeight: 700, color: 'var(--ink-muted)', letterSpacing: 0.5}}>
                    FEEDS
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({...form, feeds: [...(form.feeds || []), {feedId: '', qty: '', isCreep: false}]})
                    }
                    style={modalAddRowS}
                  >
                    + Add
                  </button>
                </div>
                {form.feeds.map((r, ri) => {
                  // Selection list excludes inactive inputs (status === 'inactive'); legacy
                  // null/blank status falls through as eligible. feedInputs itself stays
                  // unfiltered so the save-time .find() lookups above and the fiRow
                  // placeholder lookup below still resolve historical rows that already
                  // reference an inactive feed.
                  const feedsForHerd = feedInputs.filter(
                    (f) =>
                      f.status !== 'inactive' &&
                      f.category !== 'mineral' &&
                      (!form.herd || (f.herd_scope || []).includes(form.herd)),
                  );
                  const fiRow = feedInputs.find((x) => x.id === r.feedId);
                  // Editing a historical row whose feedId now points at an
                  // inactive feed: surface it as a disabled "(inactive)"
                  // option so the dropdown shows what was stored without
                  // letting the operator pick inactives for new rows.
                  const showHistoricalInactive =
                    fiRow && fiRow.status === 'inactive' && !feedsForHerd.some((f) => f.id === fiRow.id);
                  return (
                    <div
                      key={ri}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr auto',
                        gap: 6,
                        marginBottom: 6,
                        alignItems: 'center',
                      }}
                    >
                      <select
                        value={r.feedId}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            feeds: form.feeds.map((x, i) => (i === ri ? {...x, feedId: e.target.value} : x)),
                          })
                        }
                      >
                        <option value="">Select feed...</option>
                        {showHistoricalInactive && (
                          <option value={fiRow.id} disabled>
                            {fiRow.name} (inactive)
                          </option>
                        )}
                        {feedsForHerd.map((ff) => (
                          <option key={ff.id} value={ff.id}>
                            {ff.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={r.qty}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            feeds: form.feeds.map((x, i) => (i === ri ? {...x, qty: e.target.value} : x)),
                          })
                        }
                        placeholder={(fiRow ? fiRow.unit : 'qty') as string | undefined}
                      />
                      <RemoveRowButton
                        label="Remove feed row"
                        onRemove={() => setForm({...form, feeds: form.feeds.filter((_, i) => i !== ri)})}
                      />
                      {form.herd === 'mommas' && r.feedId && (
                        <label
                          style={{
                            gridColumn: '1/-1',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 12,
                            color: 'var(--text-2)',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!r.isCreep}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                feeds: form.feeds.map((x, i) => (i === ri ? {...x, isCreep: e.target.checked} : x)),
                              })
                            }
                          />
                          Creep feed (excluded from Mommas nutrition)
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Minerals */}
              <div style={{gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 10}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
                  <span style={{fontSize: 11, fontWeight: 700, color: 'var(--ink-muted)', letterSpacing: 0.5}}>
                    MINERALS
                  </span>
                  <button
                    type="button"
                    onClick={() => setForm({...form, minerals: [...(form.minerals || []), {feedId: '', lbs: ''}]})}
                    style={modalAddRowS}
                  >
                    + Add
                  </button>
                </div>
                {form.minerals.map((r, ri) => {
                  // Same selection-vs-history split as the feed dropdown above:
                  // inactive minerals are excluded from new selections but stay
                  // resolvable via the historical .find() lookups.
                  const minerals = feedInputs.filter((f) => f.status !== 'inactive' && f.category === 'mineral');
                  const miRow = feedInputs.find((x) => x.id === r.feedId);
                  const showHistoricalInactiveMineral =
                    miRow && miRow.status === 'inactive' && !minerals.some((m) => m.id === miRow.id);
                  return (
                    <div
                      key={ri}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr auto',
                        gap: 6,
                        marginBottom: 6,
                        alignItems: 'center',
                      }}
                    >
                      <select
                        value={r.feedId}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            minerals: form.minerals.map((x, i) => (i === ri ? {...x, feedId: e.target.value} : x)),
                          })
                        }
                      >
                        <option value="">Select mineral...</option>
                        {showHistoricalInactiveMineral && (
                          <option value={miRow.id} disabled>
                            {miRow.name} (inactive)
                          </option>
                        )}
                        {minerals.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={r.lbs}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            minerals: form.minerals.map((x, i) => (i === ri ? {...x, lbs: e.target.value} : x)),
                          })
                        }
                        placeholder="lbs"
                      />
                      <RemoveRowButton
                        label="Remove mineral row"
                        onRemove={() => setForm({...form, minerals: form.minerals.filter((_, i) => i !== ri)})}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Daily checks + mortality + issues — hide for add_feed source */}
              {editSource !== 'add_feed_webform' && (
                <React.Fragment>
                  <div>
                    <label style={S.label}>Fence Voltage (kV)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={form.fenceVoltage}
                      onChange={(e) => setForm({...form, fenceVoltage: e.target.value})}
                    />
                  </div>
                  <div>
                    <label style={S.label}>Waterers checked?</label>
                    {/* The shared Yes/No (I05-B): either answer solid when chosen. */}
                    <YesNo
                      aria-label="Waterers checked?"
                      value={form.waterChecked}
                      onChange={(v) => setForm({...form, waterChecked: v})}
                    />
                  </div>
                  <div>
                    <label style={S.label}>Mortality count</label>
                    <input
                      type="number"
                      min="0"
                      value={form.mortalityCount}
                      onChange={(e) => setForm({...form, mortalityCount: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                  {parseInt(form.mortalityCount) > 0 && (
                    <div>
                      <label style={S.label}>Mortality reason *</label>
                      <input
                        value={form.mortalityReason}
                        onChange={(e) => setForm({...form, mortalityReason: e.target.value})}
                      />
                    </div>
                  )}
                  <div style={{gridColumn: '1/-1'}}>
                    <label style={S.label}>Issues / Comments</label>
                    <textarea
                      value={form.issues}
                      onChange={(e) => setForm({...form, issues: e.target.value})}
                      rows={3}
                      style={{resize: 'vertical'}}
                    />
                  </div>
                </React.Fragment>
              )}
              <DailyPhotoThumbnails photos={form?.photos} />
            </div>
            <div style={{padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8}}>
              <button onClick={saveEdit} style={{...S.btnPrimary, width: 'auto'}}>
                Save
              </button>
              {editId &&
                canDeleteDailyReport(
                  authState,
                  records.find((r) => r.id === editId),
                ) && (
                  <button onClick={() => del(editId)} style={S.btnDanger}>
                    Delete
                  </button>
                )}
              <button
                onClick={() => {
                  setNotice(null);
                  setShowForm(false);
                  setEditId(null);
                  setForm(null);
                }}
                style={S.btnGhost}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CattleDailysRouter;
